// menuState.ts — P1.1 progressive-disclosure home model (Trivia Munchers 2.0).
//
// PURE, framework-free decision layer: given the player's retention state it returns WHICH home
// surfaces the Menu should render, so the first decision a new player faces is one big PLAY — not a
// wall of topic rows, recents, missions, custom entry, and an AI toggle. The MenuScene is a dumb
// consumer of this model (it owns the Phaser objects; this file owns the policy). No DOM, no Phaser,
// no Core — only the existing localStorage-backed stats/onboarding seams, so it is unit-testable and
// cannot touch determinism.
//
// New player  (intro run not yet completed): mascot + one recommended topic + ONE big PLAY (training).
//             Daily, topic grid, recents, missions list, custom entry, AI toggle are all HIDDEN.
// Returning   (intro run done): PLAY + DAILY, a horizontal topic row, exactly ONE featured mission,
//             rank behind a tap, and custom demoted to a secondary "Custom Lab".

import {
  getStats,
  getProgress,
  getDailyMissions,
  dailyDoneToday,
  liveDailyStreak,
  type Stats,
  type MissionView,
} from './stats';
import { onboardingSeen } from './onboarding';

export type HomeMode = 'new' | 'returning';

/** A topic the menu can foreground (normalized slug + human label). */
export interface TopicRef {
  normalized: string;
  label: string;
}

/** What the primary hero button should do. */
export type HomePrimaryAction = 'training' | 'play' | 'daily';

export interface HomeModel {
  mode: HomeMode;
  /** The single hero CTA. New players get START TRAINING; returning players get PLAY. */
  primary: { label: string; action: HomePrimaryAction };
  /** Daily is hidden until the intro run is done (it is meaningless before the player can play). */
  showDaily: boolean;
  dailyDone: boolean;
  dailyStreak: number;
  /** The seed/quick-pick topic grid — returning only. */
  showTopicRow: boolean;
  /** The "recently played" chips — returning only, and only when there is history. */
  showRecents: boolean;
  /** Custom topic inline on the home (false) vs. behind a secondary "Custom Lab" entry (true). */
  customBehindLab: boolean;
  /** The AI/Rival warmup toggle — returning only (new players meet the rival in training board 3). */
  showRivalToggle: boolean;
  /** Exactly ONE mission to surface on the home; null for new players or when there are none. */
  featuredMission: MissionView | null;
  /** Rank chip is a one-tap reveal for returning players, hidden for new players. */
  showRankChip: boolean;
  rankName: string;
  /** A recommended topic to foreground (encourages breadth/mastery), or null if no candidates. */
  recommendedTopic: TopicRef | null;
}

/** Inputs the host can inject (for tests); each defaults to the live localStorage-backed source. */
export interface HomeModelInput {
  /** Candidate topics to recommend from (the Menu passes its seed/quick-pick list). */
  candidates?: TopicRef[];
  stats?: Stats;
  /** Override the first-run signal (defaults to onboardingSeen()). */
  introDone?: boolean;
  /** Has the player ever finished a board? Defaults to stats.gamesPlayed > 0. */
  hasRecents?: boolean;
  /** A/B cohort for the returning hero: 'classic_home' = PLAY (control); anything else (default) = Daily. */
  variant?: string;
}

/**
 * Pick the topic to foreground. New players get a guaranteed-recognizable first candidate; returning
 * players get the candidate they have played LEAST (an unplayed topic first), nudging mastery breadth.
 * Deterministic: ties break by the candidate order the host supplied (no Math.random here).
 */
export function recommendTopic(candidates: TopicRef[], stats: Stats, mode: HomeMode): TopicRef | null {
  if (candidates.length === 0) return null;
  if (mode === 'new') return candidates[0];
  let best = candidates[0];
  let bestPlays = Infinity;
  for (const c of candidates) {
    const plays = stats.topics[c.normalized]?.plays ?? 0;
    if (plays < bestPlays) {
      bestPlays = plays;
      best = c;
      if (plays === 0) break; // an unplayed topic is the strongest recommendation; stop early
    }
  }
  return best;
}

/** Resolve the progressive-disclosure home model from the player's current retention state. */
export function homeModel(input: HomeModelInput = {}): HomeModel {
  const stats = input.stats ?? getStats();
  const introDone = input.introDone ?? onboardingSeen();
  const mode: HomeMode = introDone ? 'returning' : 'new';
  const candidates = input.candidates ?? [];

  if (mode === 'new') {
    return {
      mode,
      primary: { label: 'START TRAINING', action: 'training' },
      showDaily: false,
      dailyDone: false,
      dailyStreak: 0,
      showTopicRow: false,
      showRecents: false,
      customBehindLab: true,
      showRivalToggle: false,
      featuredMission: null,
      showRankChip: false,
      rankName: getProgress(stats).rankName,
      recommendedTopic: recommendTopic(candidates, stats, mode),
    };
  }

  const missions = getDailyMissions(stats);
  const hasRecents = input.hasRecents ?? stats.gamesPlayed > 0;
  // Daily-as-identity (treatment, the default): the returning hero IS Today's Gauntlet. The
  // 'classic_home' holdout keeps the old PLAY hero so the reposition stays honestly A/B-measurable.
  const dailyHome = (input.variant ?? 'daily_home') !== 'classic_home';
  return {
    mode,
    primary: dailyHome
      ? { label: "PLAY TODAY'S GAUNTLET", action: 'daily' }
      : { label: 'PLAY', action: 'play' },
    showDaily: true,
    dailyDone: dailyDoneToday(stats),
    dailyStreak: liveDailyStreak(stats),
    showTopicRow: true,
    showRecents: hasRecents,
    customBehindLab: true,
    showRivalToggle: true,
    featuredMission: featuredMission(missions),
    showRankChip: true,
    rankName: getProgress(stats).rankName,
    recommendedTopic: recommendTopic(candidates, stats, mode),
  };
}

/** The one mission worth showing on the home: the first still-open one, else the first (all done). */
export function featuredMission(missions: MissionView[]): MissionView | null {
  if (missions.length === 0) return null;
  return missions.find((m) => !m.done) ?? missions[0];
}
